import React, { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView, getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPageSelectors'
import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'components/Luxkit/Typography/Caption'
import Heading from 'components/Luxkit/Typography/Heading'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import Pill from 'components/Luxkit/Pill'
import Group from 'components/utils/Group'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import { includedGuestsExceededItems } from 'lib/checkout/cartReservationUtils'
import useVillasPrice from './useVillasPrice'

interface Props {
  packageView: OfferPackageOptionView;
  offer: App.Offer;
  rooms: Array<App.Occupants>;
}

function VillasPriceDetails(props: Props) {
  const { packageView, offer, rooms } = props

  const exclusivePriceColor = useHotelPriceColour(packageView?.taxesAndFees)
  const [price, value, discount] = useVillasPrice()

  const showIncludedGuestsExceeded = useMemo(
    () =>
      !packageView?.package.roomRate?.extraGuestSurcharges.length &&
      includedGuestsExceededItems(rooms || [], packageView?.package, offer),
    [packageView, rooms, offer],
  )

  return <div>
    <div>
      <Heading
        variant="heading4"
        as="span"
        colour={exclusivePriceColor}
      >
        <FormatCurrency value={price} format="roundedDollar" />
      </Heading>
      <Caption as="span" variant="large" colour="neutral-two">
        {' '}total
      </Caption>
    </div>
    {showIncludedGuestsExceeded && (
      <AdditionalGuestsPopup
        description="+ Extra guest surcharge"
        modalContent={packageView.package.roomPolicyDescription}
      />
    )}
    {!!value && (
      <Group direction="horizontal" verticalAlign="center" gap={4}>
        <Caption variant="large" weight="bold" data-testid="value-box">
          Valued up to <FormatCurrency value={value} />
        </Caption>
        {!!discount && (
          <Pill kind="primary" variant="success">{discount}%</Pill>
        )}
      </Group>
    )}
  </div>
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    packageView: getCheapestSelectedPackageOptionWithDefault(appState, state),
    offer: state.offer,
    rooms: state.rooms,
  }
})(VillasPriceDetails)
